import { useQuery } from '@tanstack/react-query'
import { buildAuthorizeUrl, createPkceChallenge, stashPkce, useAuthStore } from '@insightai/auth'
import { queryKeys, staleTimes } from '@insightai/api-client'
import { Button, ErrorState, Skeleton } from '@insightai/ui'
import { useEffect, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { config } from './config'
import { restoreSession } from './session'

/**
 * F0-57 — the console is behind Keycloak.
 *
 * On a cold load the access token is gone (memory only), so the guard first tries
 * the HttpOnly refresh cookie. Only when that fails does it start a PKCE
 * redirect, stashing the current path so the user lands where they were headed
 * rather than on a generic home page.
 */
export function AuthGuard({ children }: { children: ReactNode }) {
  const location = useLocation()
  const status = useAuthStore((state) => state.status)

  const { isLoading, isError, refetch } = useQuery({
    queryKey: queryKeys.me(),
    queryFn: restoreSession,
    staleTime: staleTimes.me,
    retry: false,
  })

  const unauthenticated = !isLoading && !isError && status !== 'authenticated'

  useEffect(() => {
    // In prototype mode `restoreSession` has already supplied a fixture profile,
    // so there is nothing to redirect to — and redirecting would send the user
    // to a Keycloak that is not running locally.
    if (config.prototypeMode) return
    if (!unauthenticated) return
    void (async () => {
      const challenge = await createPkceChallenge()
      stashPkce(challenge, `${location.pathname}${location.search}`)
      window.location.assign(
        buildAuthorizeUrl({
          authorizationEndpoint: config.keycloak.authorizationEndpoint,
          clientId: config.keycloak.clientId,
          redirectUri: config.keycloak.redirectUri,
          challenge,
        }),
      )
    })()
  }, [unauthenticated, location.pathname, location.search])

  if (isLoading || unauthenticated) {
    return (
      <div className="flex min-h-screen flex-col gap-4 p-8" aria-busy>
        <span className="sr-only" role="status">
          Checking your session
        </span>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="mx-auto flex min-h-screen max-w-lg items-center p-8">
        <ErrorState
          title="We couldn't verify your session"
          description="This is usually a temporary network problem rather than a sign-in issue."
          onRetry={() => void refetch()}
        />
      </div>
    )
  }

  return <>{children}</>
}

/**
 * The PKCE callback route.
 *
 * A state mismatch is shown as a plain restart rather than an alarming security
 * message: the commonest cause is a stale tab or a double-click on Sign In, not
 * an attack, and the corrective action is identical either way.
 */
export function AuthCallback() {
  const { isLoading, error, data } = useQuery({
    queryKey: ['auth', 'callback', window.location.search],
    retry: false,
    queryFn: async () => {
      const { validateCallback } = await import('@insightai/auth')
      const { exchangeCodeForSession, fetchProfile } = await import('./session')
      const { code, verifier, returnTo } = validateCallback(
        new URLSearchParams(window.location.search),
      )
      await exchangeCodeForSession(code, verifier)
      const profile = await fetchProfile()
      useAuthStore.getState().setUser(profile)
      return { returnTo: returnTo ?? `/w/${profile.workspaces[0]?.id ?? ''}` }
    },
  })

  useEffect(() => {
    if (data?.returnTo) window.location.replace(data.returnTo)
  }, [data])

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p role="status" className="text-body-sm text-text-secondary">
          Completing sign-in…
        </p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="mx-auto flex min-h-screen max-w-lg flex-col items-center justify-center gap-4 p-8">
        <ErrorState
          title="Sign-in could not be completed"
          description={error instanceof Error ? error.message : 'Start again from the sign-in page.'}
        />
        <Button asChild>
          <a href={`${config.marketingUrl}/auth/login`}>Back to sign in</a>
        </Button>
      </div>
    )
  }

  return null
}
